import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RUNTIME_CONFIG } from '../config/runtime-config';
import { KeycloakSessionService } from '../services/auth/keycloak-session.service';
import { hasApplicationPermission } from '../services/auth/access-claims';

export interface NotificationPreferences {
  email: boolean;
  sms: boolean;
  loginSucceeded: boolean;
  loginFailed: boolean;
  loggedOut: boolean;
}
export interface PreferenceState {
  scope: string;
  userId: string;
  revision: number;
  override: NotificationPreferences | null;
  effective: NotificationPreferences;
}
export interface PreferenceView {
  settings: PreferenceState;
  email: string | null;
  phone: string | null;
  contactStatus: string;
}
@Injectable({ providedIn: 'root' })
export class NotificationPreferencesApiService {
  private readonly session = inject(KeycloakSessionService);
  canWrite(): boolean {
    return (
      this.session.isSecurityAdministrator ||
      (hasApplicationPermission(this.session.claims, 'notifications', 'read') &&
        hasApplicationPermission(this.session.claims, 'notifications', 'write'))
    );
  }
  private readonly http = inject(HttpClient);
  private readonly base = inject(RUNTIME_CONFIG).notificationApiUrl;
  private readonly url = this.base + '/me/preferences';
  private readonly usersUrl = this.base + '/users';
  defaults() {
    return this.http.get<NotificationPreferences>(
      this.base + '/preferences/defaults',
    );
  }
  getUser(userId: string) {
    return this.http.get<PreferenceView>(
      this.usersUrl + '/' + encodeURIComponent(userId) + '/preferences',
    );
  }
  updateUser(
    userId: string,
    expectedRevision: number,
    preferences: NotificationPreferences,
  ) {
    return this.http.put<PreferenceState>(
      this.usersUrl + '/' + encodeURIComponent(userId) + '/preferences',
      { expectedRevision, preferences },
    );
  }
  get() {
    return this.http.get<PreferenceView>(this.url);
  }
  update(expectedRevision: number, preferences: NotificationPreferences) {
    return this.http.put<PreferenceState>(this.url, {
      expectedRevision,
      preferences,
    });
  }
  reset(expectedRevision: number) {
    return this.http.delete<PreferenceState>(this.url, {
      params: { expectedRevision },
    });
  }
}
